import axiosInstance from "../../lib/axiosInstance";
import { toast } from "react-toastify";
import {
  setLoadingUsers,
  setLoadingUserInfo,
  setLoadingUserCreate,
  setLoadingUserUpdate,
  setErrorLoadingUsers,
  setErrorLoadingUserInfo,
  setErrorCreatingUser,
  setErrorUpdatingUser,
  setUsers,
  userLoginSuccess,
  userLogout,
  updateUserSuccess,
  setServerResponseStatus,
  setPagination,
  createUserSuccess,
  setLoadingUser,
  setUser,
  setErrorLoadingUser,
  setLoadingUserDelete,
  deleteUserSuccess,
  setErrorDeletingUser,
} from "../slices/user";
import { mapUserToSummary } from "../../mappers/userMappers";
import { getErrorMessage } from "../../utils/errorUtils";
import { saveToken, saveUser } from "../../lib/storage";

export const getUsers =
  ({ page = 1, size = 10, search = "", role = "", status = "" } = {}) =>
  async (dispatch) => {
    dispatch(setLoadingUsers());

    try {
      const params = {
        page,
        size,
      };

      // Only add optional params if they have values
      if (search) params.search = search;
      if (role) params.role = role;
      if (status) params.status = status;

      const { data } = await axiosInstance.get("/users", {
        headers: { requiresAuth: true },
        params: params,
      });

      if (data.status === "SUCCESS") {
        dispatch(setUsers(data.data.content));
        dispatch(setPagination(data.data.pagination));
      }
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to load users";
      dispatch(setErrorLoadingUsers(message));
    }
  };

export const loginUser = (values) => async (dispatch) => {
  dispatch(setLoadingUserInfo());

  try {
    const { data } = await axiosInstance.post("/auth/login", values, {
      headers: { requiresAuth: false },
    });

    if (data.status === "SUCCESS") {
      toast.success(data.message);

      const userData = data.data.userData;
      const token = data.data.token;

      saveUser(userData);
      saveToken(token);

      dispatch(userLoginSuccess(userData));
    }
  } catch (error) {
    dispatch(setErrorLoadingUserInfo(getErrorMessage(error)));
  }
};

export const logoutUser = () => (dispatch) => {
  localStorage.removeItem("userInfo");
  localStorage.removeItem("jwtToken");

  toast.success("Logout Successful!");
  dispatch(userLogout());
};

export const createUser = (values) => async (dispatch) => {
  dispatch(setLoadingUserCreate());
  try {
    const { data } = await axiosInstance.post("/users", values, {
      headers: { requiresAuth: true },
    });
    if (data.status === "SUCCESS") {
      toast.success(data.message || "User created successfully!");

      const listUserData = mapUserToSummary(data.data);
      dispatch(createUserSuccess(listUserData));
      dispatch(
        setServerResponseStatus({
          operation: "create",
          status: data.statusCode,
        }),
      );
    }
  } catch (error) {
    const message = getErrorMessage(error);
    toast.error(message);
    dispatch(setErrorCreatingUser(message));
  }
};

export const getUserById = (id) => async (dispatch) => {
  dispatch(setLoadingUser());

  try {
    const { data } = await axiosInstance.get(`/users/${id}`, {
      headers: { requiresAuth: true },
    });

    if (data.status === "SUCCESS") {
      dispatch(setUser(data.data));
    }
  } catch (error) {
    dispatch(setErrorLoadingUser(getErrorMessage(error)));
  }
};

export const updateUser =
  ({ id, values }) =>
  async (dispatch, getState) => {
    dispatch(setLoadingUserUpdate());
    try {
      const { data } = await axiosInstance.put(`/users/${id}`, values);
      const { userInfo } = getState().user;
      if (data.status === "SUCCESS") {
        toast.success(data.message || "Profile updated successfully!");

        const detailedUserInfo = data.data;

        if (userInfo && userInfo.id === id) {
          saveUser(detailedUserInfo);
        }

        const listUserData = mapUserToSummary(detailedUserInfo);

        dispatch(updateUserSuccess(listUserData));

        dispatch(
          setServerResponseStatus({
            operation: "update",
            status: data.statusCode,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(setErrorUpdatingUser(message));
    }
  };

export const deleteUser = (id) => async (dispatch) => {
  dispatch(setLoadingUserDelete());

  try {
    const { data } = await axiosInstance.delete(`/users/${id}`, {
      headers: { requiresAuth: true },
    });

    if (data.status === "SUCCESS") {
      toast.success(data.message || "User deleted successfully!");
      dispatch(deleteUserSuccess(id));
      dispatch(
        setServerResponseStatus({
          operation: "delete",
          status: data.statusCode,
        }),
      );
    }
  } catch (error) {
    const message = getErrorMessage(error);
    toast.error(message);
    dispatch(setErrorDeletingUser(message));
  }
};

export const resetUserServerStatus = () => (dispatch) => {
  dispatch(setServerResponseStatus(null));
};

export const clearUserInfoError = () => (dispatch) => {
  dispatch(setErrorLoadingUserInfo(null));
};
